import type { ComboBoxValidationValue } from '@react-types/combobox';
import type { Node } from '@react-types/shared/src/collections';
import type { AriaComboBoxProps, AriaPopoverProps, AriaSelectProps } from 'react-aria';
import { merge } from 'lodash-es';
import * as React from 'react';
import { useMemo } from 'react';
import { HiddenSelect, useObjectRef, useSelect } from 'react-aria';
import { DialogTrigger, Popover } from 'react-aria-components';
import { mergeRefs } from 'react-merge-refs';
import { useSelectState } from 'react-stately';
import useMeasure from 'react-use-measure';

import type { FromSingleSelectionProps } from '../../common/props';
import type {
  ControlAppearance,
  ControlMaxWidth,
  ControlMinWidth,
  ControlSize,
  ControlVariant,
  ControlWidth,
} from '../../controls/shared/types';
import { type IconName, Icon } from '../../assets/Icon/Icon';
import { toSingleSelectionProps } from '../../common/props';
import { zIndexes } from '../../common/z_indexes';
import { Button } from '../../controls/Button/Button';
import { arrowsColor } from '../../controls/Button/Button.styles';
import { ControlGroup } from '../../controls/ControlGroup/ControlGroup';
import {
  useControlAppearance,
  useControlGhost,
  useControlMaxWidth,
  useControlMinWidth,
  useControlSize,
  useControlVariant,
  useControlWidth,
} from '../../controls/shared/context';
import { iconColor } from '../../controls/shared/styles';
import { controlArrowsSize, controlIconSize } from '../../controls/shared/vars';
import { colors, darkThemeSelector, shadows, styled } from '../../stitches.config';
import { maxWidthCSS } from '../../utilities/shared/MaxWidth';
import { minWidthCSS } from '../../utilities/shared/MinWidth';
import { widthCSS } from '../../utilities/shared/Width';
import { SelectValue } from '../Select/Select';
import { SelectTrigger } from '../Select/SelectTrigger';
import { ComboBoxOverlay } from './ComboBoxOverlay';

export type { Node as ComboBoxNode };

export const ComboBoxPopover = styled(Popover, {
  // Uses !important to override the default z-index of 10000 from React Aria's Popover component.
  zIndex: `${zIndexes.overlay} !important`,
  display: 'flex',
  overflow: 'auto',
  background: colors.bgApplicationLight,
  boxShadow: shadows.overlayLight,
  borderRadius: '$10',

  [darkThemeSelector]: {
    background: colors.bgApplicationDark,
    boxShadow: shadows.overlayDark,
  },
});
ComboBoxPopover.displayName = 'ComboBoxPopover';

const ComboBoxIcon = styled(Icon, {
  size: controlIconSize,
  color: iconColor,
});
ComboBoxIcon.displayName = 'ComboBoxIcon';

const ComboBoxArrows = styled(Icon, {
  size: controlArrowsSize,
  color: arrowsColor,
});
ComboBoxArrows.displayName = 'ComboBoxArrows';

const ComboBoxGroup = styled(ControlGroup, {
  width: '100%',
});
ComboBoxGroup.displayName = 'ComboBoxGroup';

const ComboBoxContainer = styled('div', DialogTrigger);
ComboBoxContainer.displayName = 'ComboBoxContainer';

interface ComboBoxProps<T> extends FromSingleSelectionProps<AriaSelectProps<T>> {
  actions?: React.ReactNode;
  appearance?: ControlAppearance;
  canClearValue?: boolean;
  defaultItems?: AriaComboBoxProps<T>['defaultItems'];
  ghost?: boolean;
  icon?: IconName;
  indeterminate?: boolean;
  invalid?: boolean;
  minSearchLength?: number;
  placement?: AriaPopoverProps['placement'];
  popoverMaxWidth?: string | number;
  ref?: React.RefObject<HTMLElement | null>;
  renderSelected?: (option: Node<T>) => React.ReactNode;
  size?: ControlSize;
  validate?: AriaSelectProps<T>['validate'];
  variant?: ControlVariant;
  width?: ControlWidth;
  maxWidth?: ControlMaxWidth;
  minWidth?: ControlMinWidth;
}

export function ComboBox<T extends object>({ ref, ...props }: ComboBoxProps<T>) {
  const renamedProps = toSingleSelectionProps(props);
  const state = useSelectState({
    ...renamedProps,
    items: props.defaultItems ?? props.items,
  });
  const [measureRef, { width: inputWidth }] = useMeasure();

  const controlAppearance = useControlAppearance(props.appearance, 'muted');
  const controlGhost = useControlGhost(props.ghost, false);
  const controlSize = useControlSize(props.size, 'medium');
  const controlVariant = useControlVariant(props.variant, 'secondary');
  const controlWidth = useControlWidth(props.width) ?? props.width;
  const controlMaxWidth = useControlMaxWidth(props.maxWidth) ?? props.maxWidth;
  const controlMinWidth = useControlMinWidth(props.minWidth) ?? props.minWidth;

  const { placement = 'bottom start', canClearValue = false, indeterminate } = renamedProps;

  const selectRef = useObjectRef(ref);
  const { valueProps, triggerProps } = useSelect(renamedProps, state, selectRef);

  const selected = useMemo(() => {
    if (!state.selectedItem) return null;
    if (props.renderSelected) return props.renderSelected(state.selectedItem);
    return state.selectedItem.rendered;
  }, [props, state.selectedItem]);

  const { validate, ...rest } = props;
  const selectTrigger = (
    <SelectTrigger
      {...triggerProps}
      ref={mergeRefs([selectRef, measureRef])}
      size={controlSize}
      appearance={controlGhost ? 'muted' : controlAppearance}
      variant={controlVariant}
      ghost={!state.isOpen && controlGhost}
      data-ghost={!state.isOpen && controlGhost}
      css={{ width: '100%', height: '100%' }}
      isDisabled={props.disabled ?? false}
      type="button"
      onKeyDown={(e: React.KeyboardEvent) => {
        if (triggerProps.isDisabled) return;

        if (e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'Enter') {
          state.open();
        } else if (e.key === 'Backspace' && canClearValue) {
          state.setSelectedKey('');
        }
      }}
      width={controlWidth}
      maxWidth={controlMaxWidth}
      minWidth={controlMinWidth}
    >
      {props.icon && <ComboBoxIcon icon={props.icon} />}
      <SelectValue {...valueProps} size={controlSize}>
        {/* eslint-disable-next-line no-nested-ternary */}
        {indeterminate ? (
          <>&ndash;</>
        ) : state.selectedItem ? (
          selected
        ) : (
          props.placeholder ?? 'Select an option'
        )}
      </SelectValue>
      <ComboBoxArrows icon="chevrons-vertical" />
    </SelectTrigger>
  );

  const validateComboBox = useMemo(() => {
    if (!validate) return undefined;
    return (value: ComboBoxValidationValue) => validate(value.selectedKey!);
  }, [validate]);

  const stylesCSS = {
    heightAll: 'min-content',
  };

  const css = merge(
    stylesCSS,
    widthCSS(controlWidth),
    maxWidthCSS(controlMaxWidth),
    minWidthCSS(controlMinWidth),
  );

  return (
    <ComboBoxContainer css={css}>
      <HiddenSelect
        isDisabled={props.disabled}
        state={state}
        triggerRef={selectRef}
        label={props.label}
        name={props.name}
      />
      {state.selectedItem && canClearValue ? (
        <ComboBoxGroup ghost={props.ghost} size={controlSize}>
          {selectTrigger}
          <Button
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              state.setSelectedKey('');
            }}
            type="button"
            appearance="muted"
            arrangement="hidden-label"
            ghost={!state.isOpen && props.ghost}
            icon="cross"
            variant="secondary"
          >
            Clear
          </Button>
        </ComboBoxGroup>
      ) : (
        selectTrigger
      )}
      <ComboBoxPopover
        triggerRef={selectRef}
        placement={placement}
        isOpen={state.isOpen}
        onOpenChange={state.setOpen}
        style={{
          minWidth: inputWidth,
          maxWidth: props.popoverMaxWidth ?? '400px',
        }}
      >
        <ComboBoxOverlay
          {...rest}
          validate={validateComboBox}
          onSelectionChange={state.setSelectedKey}
          onClose={state.close}
          selectedKey={state.selectedItem?.key}
          isOpen={state.isOpen}
          label={props.label || props.placeholder}
          disabledKeys={props.disabledKeys}
        />
      </ComboBoxPopover>
    </ComboBoxContainer>
  );
}

export { Item as ComboBoxItem, Section as ComboBoxSection } from 'react-stately';
export type { ComboBoxProps };
